<template>
  <v-data-table
    :headers="computedHeaders"
    :items="projects"
    item-key="id"
    class="gradient-info__table"
    :options="options"
    :server-items-length="totalElements"
    :loading="loading"
    :footer-props="{ itemsPerPageOptions: [50, 100, 500, -1] }"
    :mobile-breakpoint="0"
    @update:options="v => $emit('update:options', v)"
  >
    <template v-slot:item="{ item }">
      <tr class="project-row">
        <td v-for="h in computedHeaders" :key="h.value">
          <span v-if="h.value === 'name'" class="font-weight-bold">{{ item.name }}</span>
          <span v-else-if="h.value === 'dueDate' && item.earliestDeadline" :class="dueDateClass(item.earliestDeadline)">
            {{ $formatDateNoTime(item.earliestDeadline) }}
          </span>
        </td>
      </tr>
      <tr v-for="offer in item.offers" :key="`o${offer.id}`" class="offer-row" @click="$emit('open', offer)">
        <td v-for="h in computedHeaders" :key="h.value" :class="h.align === 'end' && 'text-right'">
          <template v-if="h.value === 'name'">
            <span class="pl-4">{{ offer.code || '–' }}</span>
            <div v-if="$vuetify.breakpoint.xsOnly" class="caption grey--text text--darken-1 pl-4">{{ offer.partyName }}</div>
          </template>
          <template v-else-if="h.value === 'partyName'">{{ offer.partyName || '–' }}</template>
          <template v-else-if="h.value === 'status'">
            <quotation-status-chip :status="offer.status" />
            <div v-if="offer.sentDate && isActive(offer)" class="caption grey--text text--darken-1">
              {{ $t('quotations.recipients.documents.sent_on', { date: $formatDateNoTime(offer.sentDate) }) }}
            </div>
          </template>
          <template v-else-if="h.value === 'dueDate'">
            <span v-if="offer.deadline" :class="!offer.sentDate && isActive(offer) && dueDateClass(offer.deadline)">{{ deadlineText(offer.deadline, offer.deadlineTime) }}</span>
          </template>
          <template v-else-if="h.value === 'result'">{{ offer.result ? $t(`quotations.recipients.results.${offer.result}`) : '–' }}</template>
          <span v-else-if="h.value === 'total'" class="text-no-wrap">{{ offer.total ? $formatNumber(offer.total, '0,0') + ' €' : '–' }}</span>
          <template v-else-if="h.value === 'updatedAt'">{{ $formatDateNoTime(offer.updatedAt) }}</template>
          <v-menu v-else-if="h.value === 'actions'" offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" :title="$t('quotations.actions.menu')" @click.stop>
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="$emit('open', offer)">
                <v-list-item-icon><v-icon>mdi-open-in-app</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('quotations.row_menu.open') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="$emit('copy', { project: item, offer })">
                <v-list-item-icon><v-icon>mdi-content-copy</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('quotations.row_menu.copy') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </td>
      </tr>
    </template>
    <template v-slot:no-data>
      <no-content-view
        :title="$t('quotations.no_quotations')"
        :description="$t('quotations.no_quotations_help')"
        title-icon="mdi-file-document-edit-outline"
        :reload-button="false"
      />
    </template>
  </v-data-table>
</template>

<script>
import dayjs from 'dayjs';
import { ACTIVE_STATUSES } from '@/helpers/quotationStatus';
import { deadlineText } from '@/helpers/quotationDeadline';
import QuotationStatusChip from './QuotationStatusChip';
import NoContentView from '@/components/NoContentView';

// Tarjottavat projektit ja niiden alla projektin tarjoukset. Palvelin sivuttaa ja järjestää projektit
// (nimi, aikaisin avoin jättöpäivä tai viimeksi päivitetty tarjous).
export default {
  name: 'QuotationProjectsTable',
  components: { QuotationStatusChip, NoContentView },
  props: {
    projects: { type: Array, default: () => [] },
    totalElements: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    options: { type: Object, required: true },
  },
  data() {
    return {
      headers: [
        { text: this.$t('quotations.project_and_offer'), value: 'name', mobile: true },
        { text: this.$t('quotations.recipients.party'), value: 'partyName', sortable: false, mobile: false },
        { text: this.$t('quotations.status'), value: 'status', sortable: false, mobile: true },
        { text: this.$t('quotations.recipients.deadline'), value: 'dueDate', mobile: false },
        { text: this.$t('quotations.recipients.result'), value: 'result', sortable: false, mobile: false },
        { text: this.$t('quotations.total'), value: 'total', sortable: false, align: 'end', mobile: true },
        { text: this.$t('quotations.updated'), value: 'updatedAt', mobile: false },
        { text: '', value: 'actions', sortable: false, align: 'end', mobile: false },
      ],
    };
  },
  computed: {
    computedHeaders() {
      return this.$vuetify.breakpoint.xsOnly ? this.headers.filter(h => h.mobile) : this.headers;
    },
  },
  methods: {
    deadlineText,
    isActive(offer) {
      return ACTIVE_STATUSES.includes(offer.status);
    },
    dueDateClass(date) {
      const days = dayjs(date).diff(dayjs().startOf('day'), 'day');
      return days < 3 ? 'error--text font-weight-bold' : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.project-row {
  background-color: rgba(0, 0, 0, 0.03);
}
.offer-row {
  cursor: pointer;
}
</style>
